"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Menu, X, Phone, Mail, ChevronDown } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import Image from "next/image"
import { CONTACT } from "@/lib/constants"
import QuoteModal from "./quote-modal"
import { serviceDetails } from "@/lib/services-data"


export function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const [quoteModalOpen, setQuoteModalOpen] = useState(false)
  const pathname = usePathname()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false)
    setServicesOpen(false)
  }, [pathname])

  const navigation = [
    { name: "Services", href: "/services", hasDropdown: true },
    { name: "Locations", href: "/locations" },
    { name: "Why Us", href: "/why-us" },
    { name: "About", href: "/about" },
    { name: "FAQ", href: "/faq" },
    { name: "Contact", href: "/contact" },
  ]

  const servicesDropdown = serviceDetails.map(service => (
    {
      name: service.title,
      href: service.slug
    }
  )).slice(0, 6)

  const isActive = (href: string) => pathname === href

  return (
    <>
      {/* Top Bar - Contact Info */}
      <div className="hidden lg:block bg-gradient-to-r from-[#003DA5] to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-2 text-sm">
            <div className="flex items-center gap-6">
              <a
                href={`tel:${CONTACT.phone.primary}`}
                className="flex items-center gap-2 hover:text-orange-300 transition-colors"
              >
                <Phone size={14} />
                <span className="font-medium">{CONTACT.phone.display}</span>
              </a>
              <a
                href={`mailto:${CONTACT.email.primary}`}
                className="flex items-center gap-2 hover:text-orange-300 transition-colors"
              >
                <Mail size={14} />
                <span>{CONTACT.email.primary}</span>
              </a>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <Link href="/about" className="flex items-center gap-1.5 hover:text-orange-300 transition-colors">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="font-medium">Trusted Since 2014</span>
              </Link>
              <span className="text-blue-400/50">|</span>
              <Link href="/why-us" className="flex items-center gap-1.5 hover:text-orange-300 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="font-medium">100% Insured Moves</span>
              </Link>
              <span className="text-blue-400/50">|</span>
              <Link href="/contact" className="flex items-center gap-1.5 hover:text-orange-300 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">24/7 Support Available</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled
            ? "bg-white shadow-lg border-b border-slate-200"
            : "bg-white/95 backdrop-blur-md border-b border-slate-100"
          }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <motion.div
              className="flex items-center flex-shrink-0"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Link href="/" className="flex items-center">
                <Image
                  src="/logo.png"
                  alt="Bigwin Relocation - Professional Packers and Movers"
                  width={180}
                  height={60}
                  className="h-12 w-auto"
                  priority
                  quality={90}
                  sizes="180px"
                />
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navigation.map((item) => (
                <div key={item.name} className="relative group">
                  {item.hasDropdown ? (
                    <>
                      <Link
                        href={item.href}
                        className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all text-sm ${isActive(item.href)
                            ? "text-[#FF7A00] bg-orange-50"
                            : "text-slate-700 hover:text-[#FF7A00] hover:bg-slate-50"
                          }`}
                      >
                        {item.name}
                        <ChevronDown size={16} className="group-hover:rotate-180 transition-transform duration-300" />
                      </Link>

                      {/* Dropdown Menu */}
                      <div className="absolute top-full left-0 mt-1 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                        <div className="bg-white rounded-xl shadow-xl border border-slate-200 py-2 overflow-hidden">
                          {servicesDropdown.map((service) => (
                            <Link
                              key={service.name}
                              href={`/services/${service.href}`}
                              className="block px-4 py-3 text-sm text-slate-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-blue-50 hover:text-[#FF7A00] transition-all"
                            >
                              {service.name}
                            </Link>
                          ))}
                          <div className="border-t border-slate-100 mt-2 pt-2">
                            <Link
                              href="/services"
                              className="block px-4 py-2 text-sm font-semibold text-[#003DA5] hover:text-[#FF7A00] transition-colors"
                            >
                              View All Services →
                            </Link>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      href={item.href}
                      className={`block px-4 py-2 rounded-lg font-medium transition-all text-sm ${isActive(item.href)
                          ? "text-[#FF7A00] bg-orange-50"
                          : "text-slate-700 hover:text-[#FF7A00] hover:bg-slate-50"
                        }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link href="/complaint">
                  <Button
                    variant="outline"
                    className="border-2 border-[#003DA5] text-[#003DA5] hover:bg-[#003DA5] hover:text-white font-semibold transition-all"
                  >
                    File Complaint
                  </Button>
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={() => setQuoteModalOpen(true)}
                  className="bg-gradient-to-r from-[#FF7A00] to-orange-600 hover:from-orange-600 hover:to-[#FF7A00] text-white font-semibold shadow-lg px-6"
                >
                  Get Free Quote
                </Button>
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Toggle navigation menu"
              aria-expanded={isOpen}
              whileTap={{ scale: 0.95 }}
            >
              {isOpen ? (
                <X size={24} className="text-slate-700" />
              ) : (
                <Menu size={24} className="text-slate-700" />
              )}
            </motion.button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className="lg:hidden border-t border-slate-200"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="py-4 space-y-1">
                  {/* Mobile Contact Info */}
                  <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-orange-50 rounded-lg mb-3">
                    <a
                      href={`tel:${CONTACT.phone.primary}`}
                      className="flex items-center gap-2 text-sm font-medium text-[#003DA5] mb-2"
                    >
                      <Phone size={16} />
                      {CONTACT.phone.display}
                    </a>
                    <a
                      href={`mailto:${CONTACT.email.primary}`}
                      className="flex items-center gap-2 text-xs text-slate-600"
                    >
                      <Mail size={14} />
                      {CONTACT.email.primary}
                    </a>
                  </div>

                  {navigation.map((item) => (
                    <div key={item.name}>
                      {item.hasDropdown ? (
                        <>
                          <button
                            onClick={() => setServicesOpen(!servicesOpen)}
                            className="w-full flex items-center justify-between px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-sm font-medium"
                          >
                            {item.name}
                            <ChevronDown
                              size={16}
                              className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`}
                            />
                          </button>
                          <AnimatePresence>
                            {servicesOpen && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="pl-4 space-y-1"
                              >
                                {servicesDropdown.map((service) => (
                                  <Link
                                    key={service.name}
                                    href={service.href}
                                    className="block px-4 py-2 text-sm text-slate-600 hover:text-[#FF7A00] hover:bg-orange-50 rounded-lg transition-colors"
                                  >
                                    {service.name}
                                  </Link>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        <Link
                          href={item.href}
                          className={`block px-4 py-3 rounded-lg transition-colors text-sm font-medium ${isActive(item.href)
                              ? "text-[#FF7A00] bg-orange-50"
                              : "text-slate-700 hover:bg-slate-50"
                            }`}
                        >
                          {item.name}
                        </Link>
                      )}
                    </div>
                  ))}

                  {/* Mobile CTA Buttons */}
                  <div className="pt-4 space-y-2 px-4">
                    <Link href="/complaint">
                      <Button
                        variant="outline"
                        className="w-full border-2 border-[#003DA5] text-[#003DA5] hover:bg-[#003DA5] hover:text-white font-semibold"
                      >
                        File Complaint
                      </Button>
                    </Link>
                    <Button
                      onClick={() => setQuoteModalOpen(true)}
                      className="w-full bg-gradient-to-r from-[#FF7A00] to-orange-600 text-white font-semibold shadow-lg"
                    >
                      Get Free Quote
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </header>
      <QuoteModal
        quoteModalOpen={quoteModalOpen}
        setQuoteModalOpen={setQuoteModalOpen}
      />
    </>
  )
}
